import { Box, Grid, makeStyles } from '@material-ui/core';
import { CategoryDto, ProductDto, StoreCategory } from 'types';
import { DraggableProductListItem } from './DraggableProductListItem';
import { DraggableProductListItemOverlay } from './DraggableProductListItemOverlay';
import {
    makeCatalogChangeCategoryProductsOrderAction,
    makeCatalogChangeCategoryProductsOrderSuccessAction,
} from 'state/catalog/actions';
import React, { MutableRefObject, useCallback, useState } from 'react';
import { SortableContext } from '@dnd-kit/sortable';
import { Text, TextTypography } from 'components/Text/Text';
import { useAction } from 'core/store/store';
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    SensorDescriptor,
    SensorOptions,
    useDroppable,
} from '@dnd-kit/core';
import i18n from '../../../i18n';

export interface CategoryProps {
    category: CategoryDto;
    categoryProducts: ProductDto[];
    sensors: SensorDescriptor<SensorOptions>[];
    anchorRef: MutableRefObject<Record<string, HTMLDivElement>>;
    // Optional:
    storeCategory?: StoreCategory;
    onProductClick?: (productId: string, categoryId: string) => void;
}

const useStyles = makeStyles(theme => ({
    categoryContainer: {
        paddingTop: 16,
        [theme.breakpoints.down(640)]: {
            '&:last-child': {
                paddingBottom: 72,
            },
        },
    },
    categoryName: {
        paddingLeft: 16,
    },
    listItem: {
        flex: '0 0 50%',
        minWidth: 0,
        [theme.breakpoints.down(430)]: {
            flex: '0 0 100%',
        },
        [theme.breakpoints.between(430, 640)]: {
            flex: '0 0 50%',
        },
        [theme.breakpoints.between(640, 968)]: {
            flex: '0 0 33.33%',
        },
        [theme.breakpoints.between(968, 1920)]: {
            flex: '0 0 25%',
        },
        [theme.breakpoints.up(1920)]: {
            flex: '0 0 20%',
        },
        padding: 16,
    },
    categoryHeader: {
        cursor: 'pointer',
    },
}));

export const DraggableCategory = (props: CategoryProps) => {
    const { category, categoryProducts, sensors, anchorRef } = props;

    const { t } = i18n.useTranslation();
    const classes = useStyles();
    const action = useAction();

    const [dragableItem, setDraggableItem] = useState<ProductDto>(null);

    const { setNodeRef: productDropZoneRef } = useDroppable({
        id: 'droppableProduct',
    });

    const onProductClick = useCallback(
        (productId, categoryId) => props.onProductClick && props.onProductClick(productId, categoryId),
        [props.onProductClick],
    );

    const anchorRefUpdate = useCallback(
        r => {
            anchorRef.current[category.categoryId] = r;
        },
        [anchorRef.current, category],
    );

    const handleDragStart = useCallback(
        (event: DragStartEvent) => {
            setDraggableItem(categoryProducts.find(product => product.productId === event.active.id));
        },
        [categoryProducts, setDraggableItem],
    );

    const handleDragEnd = useCallback(
        ({ active, over }: DragEndEvent) => {
            if (!over || active.id === over.id) {
                return;
            }

            action(makeCatalogChangeCategoryProductsOrderAction, active.id, over.id);

            let currentDragableItemIndex: number;
            let directionItemIndex: number;

            categoryProducts.some((product, index) => {
                if (currentDragableItemIndex != null && directionItemIndex != null) {
                    return true;
                }
                if (product.productId === active.id) {
                    currentDragableItemIndex = index;
                }
                if (product.productId === over.id) {
                    directionItemIndex = index;
                }
            });

            const movedItemEntity = categoryProducts.splice(currentDragableItemIndex, 1);
            categoryProducts.splice(directionItemIndex, 0, movedItemEntity[0]);
            action(makeCatalogChangeCategoryProductsOrderSuccessAction, { [category.categoryId]: categoryProducts });
            setDraggableItem(null);
        },
        [categoryProducts, category.categoryId, setDraggableItem],
    );

    return (
        <div
            ref={anchorRefUpdate}
            className={classes.categoryContainer}
            itemProp="itemListElement"
            itemType="http://schema.org/ListItem"
        >
            <Box mb={2}>
                <Text
                    className={classes.categoryName}
                    typography={TextTypography.LEAD_STRONG}
                    shouldCollapseText={true}
                    itemProp="name"
                >
                    {t(category.name)}
                </Text>
            </Box>
            <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                <Grid innerRef={productDropZoneRef} container direction={'row'} justify={'flex-start'} wrap={'wrap'}>
                    <SortableContext items={categoryProducts.map(product => product.productId)}>
                        {categoryProducts.map(product => (
                            <div className={classes.listItem} key={product.productId}>
                                <DraggableProductListItem
                                    product={product}
                                    storeCategory={props.storeCategory}
                                    onProductClick={onProductClick}
                                />
                            </div>
                        ))}
                    </SortableContext>
                    <DragOverlay>
                        {dragableItem ? (
                            <div className={classes.listItem}>
                                <DraggableProductListItemOverlay
                                    product={dragableItem}
                                    storeCategory={props.storeCategory}
                                />
                            </div>
                        ) : null}
                    </DragOverlay>
                </Grid>
            </DndContext>
        </div>
    );
};
